import { useState, useMemo, useRef } from 'react';
import Sidebar from '../../components/feature/Sidebar';
import { useFinanceRecords } from '../../store/financeStore';
import { useCustomers } from '../../store/customerStore';
import HistoryDetailModal from './components/HistoryDetailModal';
import { historySubmissionRecords } from '../../mocks/submissionHistory';
import type { HistorySubmissionRecord } from '../../mocks/submissionHistory';

interface DataPreview {
  variety: string;
  openingStock: number;
  purchase: number;
  sales: number;
  closingStock: number;
  grainNature: string;
  storageLocation: string;
  storageMethod: string;
  moistureContent: string;
  impurityRate: string;
  unsoundKernel: string;
  purchaseSource: string;
  salesTarget: string;
  purchaseDate: string;
  salesDate: string;
  purchasePrice: string;
  salesPrice: string;
  settlementMethod: string;
  qualityGrade: string;
  storageQuality: string;
  foodSafety: string;
  remark: string;
  // 关联真实数据
  purchaseRecords: Array<{
    counterpart: string;
    amount: number;
    date: string;
    unitPrice: number;
    settlement: string;
  }>;
  salesRecords: Array<{
    counterpart: string;
    amount: number;
    date: string;
    unitPrice: number;
    settlement: string;
  }>;
}

// 期初库存配置（按品种，可扩展）
const OPENING_STOCK: Record<string, number> = {
  小麦: 1200,
  玉米: 2500,
  稻谷: 800,
  大米: 600,
  大豆: 300,
  高粱: 150,
};

const STORAGE_INFO: Record<
  string,
  {
    location: string;
    method: string;
    quality: string;
    safety: string;
    unsound: string;
  }
> = {
  小麦: {
    location: '1号仓库（A区）',
    method: '散装平房仓储存',
    quality: '宜存',
    safety: '合格（真菌毒素未检出）',
    unsound: '3.2%',
  },
  玉米: {
    location: '2号仓库（B区）',
    method: '钢板仓机械通风储存',
    quality: '宜存',
    safety: '合格',
    unsound: '4.5%',
  },
  稻谷: {
    location: '3号仓库（C区）',
    method: '低温准低温储存',
    quality: '宜存',
    safety: '合格（重金属未超标）',
    unsound: '2.8%',
  },
  大米: {
    location: '3号仓库（C区）',
    method: '低温准低温储存',
    quality: '宜存',
    safety: '合格',
    unsound: '2.0%',
  },
  大豆: {
    location: '4号仓库（D区）',
    method: '散装平房仓储存',
    quality: '宜存',
    safety: '合格',
    unsound: '2.0%',
  },
  高粱: {
    location: '5号仓库（E区）',
    method: '散装平房仓储存',
    quality: '宜存',
    safety: '合格',
    unsound: '3.5%',
  },
};

export default function Submission() {
  const { records: financeRecords } = useFinanceRecords();
  const { customers } = useCustomers();

  const [activeTab, setActiveTab] = useState<'new' | 'history' | 'settings'>('new');
  const [reportType, setReportType] = useState('月度报送');
  const [reportPeriod, setReportPeriod] = useState('2026年1月');
  const [department, setDepartment] = useState('省级粮储部门');
  const [isGenerating, setIsGenerating] = useState(false);
  const [reminderDays, setReminderDays] = useState<number[]>([7, 3, 1]);

  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<DataPreview | null>(null);
  const [editingRemark, setEditingRemark] = useState('');
  const [remarkSaved, setRemarkSaved] = useState(false);
  const [remarkMap, setRemarkMap] = useState<Record<string, string>>({});

  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [exportingPDF, setExportingPDF] = useState(false);
  const previewContentRef = useRef<HTMLDivElement>(null);

  const [historyDetailOpen, setHistoryDetailOpen] = useState(false);
  const [selectedHistory, setSelectedHistory] = useState<HistorySubmissionRecord | null>(null);

  const [submitMode, setSubmitMode] = useState<'api' | 'email'>('api');
  const [apiConfig, setApiConfig] = useState({
    ip: '192.168.1.100',
    port: '8080',
    path: '/api/grain/submit',
    secretKey: 'sk-xxxxxxxxxxxxxxxxxxxxxxxx',
    timeout: '30',
    protocol: 'HTTPS',
  });
  const [emailConfig, setEmailConfig] = useState({
    toEmail: 'grain-report@province.gov.cn',
    ccEmail: '',
    subject: '粮食经营台账数据报送 - {企业名称} - {报送周期}',
    senderName: '粮食经营台账管理系统',
  });
  const [apiTestStatus, setApiTestStatus] = useState<
    'idle' | 'testing' | 'success' | 'fail'
  >('idle');
  const [emailTestStatus, setEmailTestStatus] = useState<
    'idle' | 'testing' | 'success' | 'fail'
  >('idle');
  const [configSaved, setConfigSaved] = useState(false);

  // ===== 动态聚合报送数据 =====
  const dataPreview = useMemo<DataPreview[]>(() => {
    // 按品种分组
    const varietyMap: Record<
      string,
      {
        purchaseQty: number;
        salesQty: number;
        purchaseRecords: Array<{
          counterpart: string;
          amount: number;
          date: string;
          unitPrice: number;
          settlement: string;
        }>;
        salesRecords: Array<{
          counterpart: string;
          amount: number;
          date: string;
          unitPrice: number;
          settlement: string;
        }>;
        moistures: string[];
        impurities: string[];
        grades: string[];
      }
    > = {};

    financeRecords.forEach((r) => {
      if (!varietyMap[r.variety]) {
        varietyMap[r.variety] = {
          purchaseQty: 0,
          salesQty: 0,
          purchaseRecords: [],
          salesRecords: [],
          moistures: [],
          impurities: [],
          grades: [],
        };
      }
      const v = varietyMap[r.variety];
      if (r.type === '收购') {
        v.purchaseQty += r.quantity;
        v.purchaseRecords.push({
          counterpart: r.source || r.counterpart || '',
          amount: r.amount,
          date: r.date,
          unitPrice: r.unitPrice,
          settlement: r.settlementMethod,
        });
        if (r.moistureContent) v.moistures.push(r.moistureContent);
        if (r.impurityRate) v.impurities.push(r.impurityRate);
        if (r.qualityGrade) v.grades.push(r.qualityGrade);
      } else {
        v.salesQty += r.quantity;
        v.salesRecords.push({
          counterpart: r.source || r.counterpart || '',
          amount: r.amount,
          date: r.date,
          unitPrice: r.unitPrice,
          settlement: r.settlementMethod,
        });
      }
    });

    return Object.entries(varietyMap).map(([variety, data]) => {
      const opening = OPENING_STOCK[variety] ?? 0;
      const closing = opening + data.purchaseQty - data.salesQty;
      const storageInfo =
        STORAGE_INFO[variety] ?? {
          location: '仓库待分配',
          method: '散装平房仓储存',
          quality: '宜存',
          safety: '合格',
          unsound: '-',
        };

      // 取最新收购/销售记录的价格和日期
      const latestPurchase = data.purchaseRecords
        .slice()
        .sort((a, b) => b.date.localeCompare(a.date))[0];
      const latestSales = data.salesRecords
        .slice()
        .sort((a, b) => b.date.localeCompare(a.date))[0];

      // 粮食来源：取所有收购方名称（去重）
      const purchaseSources = [
        ...new Set(data.purchaseRecords.map((r) => r.counterpart)),
      ].filter(Boolean);
      // 粮食去向：取所有销售方名称（去重）
      const salesTargets = [
        ...new Set(data.salesRecords.map((r) => r.counterpart)),
      ].filter(Boolean);

      // 从客户管理中补充来源信息
      const enrichedSources = purchaseSources.map((name) => {
        const customer = customers.find((c) => c.name === name);
        return customer ? `${name}（${customer.address || ''}）` : name;
      });
      const enrichedTargets = salesTargets.map((name) => {
        const customer = customers.find((c) => c.name === name);
        return customer ? `${name}（${customer.address || ''}）` : name;
      });

      const moisture =
        data.moistures.length > 0 ? `${data.moistures[0]}%` : '-';
      const impurity =
        data.impurities.length > 0 ? `${data.impurities[0]}%` : '-';
      const grade = data.grades.length > 0 ? data.grades[0] : '二等品';

      return {
        variety,
        openingStock: opening,
        purchase: data.purchaseQty,
        sales: data.salesQty,
        closingStock: Math.max(0, closing),
        grainNature: '商品粮',
        storageLocation: storageInfo.location,
        storageMethod: storageInfo.method,
        moistureContent: moisture,
        impurityRate: impurity,
        unsoundKernel: storageInfo.unsound,
        purchaseSource: enrichedSources.join('；') || '-',
        salesTarget: enrichedTargets.join('；') || '-',
        purchaseDate: latestPurchase?.date ?? '-',
        salesDate: latestSales?.date ?? '-',
        purchasePrice: latestPurchase
          ? `${latestPurchase.unitPrice.toLocaleString()} 元/吨`
          : '-',
        salesPrice: latestSales
          ? `${latestSales.unitPrice.toLocaleString()} 元/吨`
          : '-',
        settlementMethod:
          latestPurchase?.settlement ?? latestSales?.settlement ?? '-',
        qualityGrade: grade,
        storageQuality: storageInfo.quality,
        foodSafety: storageInfo.safety,
        remark: remarkMap[variety] ?? '',
        purchaseRecords: data.purchaseRecords,
        salesRecords: data.salesRecords,
      };
    });
  }, [financeRecords, customers, remarkMap]);

  // ===== 动态统计卡片数据 =====
  const stats = useMemo(() => {
    const totalPurchase = financeRecords
      .filter((r) => r.type === '收购')
      .reduce((s, r) => s + r.quantity, 0);
    const totalSales = financeRecords
      .filter((r) => r.type === '销售')
      .reduce((s, r) => s + r.quantity, 0);
    const totalVarieties = new Set(financeRecords.map((r) => r.variety)).size;
    const totalAmount = financeRecords.reduce((s, r) => s + r.amount, 0);
    return { totalPurchase, totalSales, totalVarieties, totalAmount };
  }, [financeRecords]);

  const handleOpenDetail = (item: DataPreview) => {
    setSelectedItem(item);
    setEditingRemark(item.remark);
    setRemarkSaved(false);
    setDetailModalOpen(true);
  };

  const handleSaveRemark = () => {
    if (!selectedItem) return;
    setRemarkMap((prev) => ({
      ...prev,
      [selectedItem.variety]: editingRemark,
    }));
    setSelectedItem({ ...selectedItem, remark: editingRemark });
    setRemarkSaved(true);
    setTimeout(() => setRemarkSaved(false), 2500);
  };

  const handleGenerate = () => {
    setIsGenerating(true);
    setTimeout(() => setIsGenerating(false), 2000);
  };

  const toggleReminder = (day: number) => {
    if (reminderDays.includes(day)) {
      setReminderDays(reminderDays.filter((d) => d !== day));
    } else {
      setReminderDays([...reminderDays, day].sort((a, b) => b - a));
    }
  };

  const handleTestApi = () => {
    setApiTestStatus('testing');
    setTimeout(() => {
      setApiTestStatus('success');
      setTimeout(() => setApiTestStatus('idle'), 3000);
    }, 1800);
  };

  const handleTestEmail = () => {
    setEmailTestStatus('testing');
    setTimeout(() => {
      setEmailTestStatus('success');
      setTimeout(() => setEmailTestStatus('idle'), 3000);
    }, 1800);
  };

  const handleSaveConfig = () => {
    setConfigSaved(true);
    setTimeout(() => setConfigSaved(false), 2500);
  };

  const handleOpenHistoryDetail = (record: HistorySubmissionRecord) => {
    setSelectedHistory(record);
    setHistoryDetailOpen(true);
  };

  const handleExportPreviewPDF = async () => {
    if (!previewContentRef.current) return;
    setExportingPDF(true);
    try {
      const { default: html2canvas } = await import('html2canvas');
      const { jsPDF } = await import('jspdf');

      const element = previewContentRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pdfWidth - 20;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 10;

      pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight - 20;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight + 10;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight - 20;
      }

      pdf.save(`报送文件_${reportPeriod}_${reportType}.pdf`);
    } catch {
      window.print();
    } finally {
      setExportingPDF(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 ml-64">
        <div className="p-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              数据报送
            </h1>
            <p className="text-gray-500">
              按规定向粮储部门报送基本数据
            </p>
          </div>

          {/* 报送概览统计卡片 — 动态数据 */}
          <div className="grid grid-cols-4 gap-6 mb-8">
            <div className="bg-gradient-to-br from-[#0c7362]/10 to-[#0c7362]/5 rounded-2xl p-6 border border-[#0c7362]/20">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#0c7362] to-[#085b4d] rounded-xl flex items-center justify-center">
                  <i className="ri-arrow-down-circle-line text-2xl text-white"></i>
                </div>
                <span className="text-sm font-medium text-[#0c7362] bg-[#0c7362]/10 px-3 py-1 rounded-full whitespace-nowrap">
                  收购
                </span>
              </div>
              <div className="text-3xl font-bold text-gray-800 mb-1">
                {stats.totalPurchase.toLocaleString()}
                <span className="text-base font-normal ml-1">吨</span>
              </div>
              <div className="text-sm text-gray-500">累计收购量</div>
            </div>

            <div className="bg-gradient-to-br from-orange-500/10 to-orange-500/5 rounded-2xl p-6 border border-orange-500/20">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center">
                  <i className="ri-arrow-up-circle-line text-2xl text-white"></i>
                </div>
                <span className="text-sm font-medium text-orange-600 bg-orange-500/10 px-3 py-1 rounded-full whitespace-nowrap">
                  销售
                </span>
              </div>
              <div className="text-3xl font-bold text-gray-800 mb-1">
                {stats.totalSales.toLocaleString()}
                <span className="text-base font-normal ml-1">吨</span>
              </div>
              <div className="text-sm text-gray-500">累计销售量</div>
            </div>

            <div className="bg-gradient-to-br from-amber-500/10 to-amber-500/5 rounded-2xl p-6 border border-amber-500/20">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center">
                  <i className="ri-seedling-line text-2xl text-white"></i>
                </div>
                <span className="text-sm font-medium text-amber-600 bg-amber-500/10 px-3 py-1 rounded-full whitespace-nowrap">
                  品种
                </span>
              </div>
              <div className="text-3xl font-bold text-gray-800 mb-1">
                {stats.totalVarieties}
                <span className="text-base font-normal ml-1">种</span>
              </div>
              <div className="text-sm text-gray-500">经营粮食品种</div>
            </div>

            <div className="bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 rounded-2xl p-6 border border-emerald-500/20">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center">
                  <i className="ri-money-cny-circle-line text-2xl text-white"></i>
                </div>
                <span className="text-sm font-medium text-emerald-600 bg-emerald-500/10 px-3 py-1 rounded-full whitespace-nowrap">
                  金额
                </span>
              </div>
              <div className="text-3xl font-bold text-gray-800 mb-1">
                {(stats.totalAmount / 10000).toFixed(0)}
                <span className="text-base font-normal ml-1">万元</span>
              </div>
              <div className="text-sm text-gray-500">累计交易金额</div>
            </div>
          </div>

          {/* 标签页导航 */}
          <div className="bg-white rounded-2xl mb-6 border border-gray-100">
            <div className="flex border-b border-gray-200">
              {[
                { key: 'new', label: '新建报送', icon: 'ri-add-circle-line' },
                { key: 'history', label: '报送历史', icon: 'ri-history-line' },
                { key: 'settings', label: '提醒设置', icon: 'ri-settings-3-line' },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() =>
                    setActiveTab(tab.key as 'new' | 'history' | 'settings')
                  }
                  className={`flex-1 px-6 py-4 text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    activeTab === tab.key
                      ? 'text-[#0c7362] border-b-2 border-[#0c7362] bg-[#0c7362]/5'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <i className={`${tab.icon} mr-2`}></i>
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* 新建报送 */}
          {activeTab === 'new' && (
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-8 border border-gray-100">
                <h3 className="text-lg font-bold text-gray-800 mb-6 flex items-center">
                  <div className="w-8 h-8 bg-[#0c7362]/10 rounded-lg flex items-center justify-center mr-3">
                    <i className="ri-file-edit-line text-[#0c7362]"></i>
                  </div>
                  报送信息配置
                </h3>
                <div className="grid grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <i className="ri-file-list-3-line text-[#0c7362] mr-1"></i>
                      报送类型
                    </label>
                    <select
                      value={reportType}
                      onChange={(e) => setReportType(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#0c7362] focus:border-transparent text-sm cursor-pointer"
                    >
                      <option>月度报送</option>
                      <option>季度报送</option>
                      <option>年度报送</option>
                      <option>专项报送</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <i className="ri-calendar-line text-[#0c7362] mr-1"></i>
                      报送周期
                    </label>
                    <select
                      value={reportPeriod}
                      onChange={(e) => setReportPeriod(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#0c7362] focus:border-transparent text-sm cursor-pointer"
                    >
                      <option>2026年1月</option>
                      <option>2025年12月</option>
                      <option>2025年11月</option>
                      <option>2025年Q4</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <i className="ri-government-line text-[#0c7362] mr-1"></i>
                      报送部门
                    </label>
                    <select
                      value={department}
                      onChange={(e) => setDepartment(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#0c7362] focus:border-transparent text-sm cursor-pointer"
                    >
                      <option>省级粮储部门</option>
                      <option>市级粮储部门</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* 数据预览 — 动态聚合 */}
              <div className="bg-white rounded-2xl p-8 border border-gray-100">
                <h3 className="text-lg font-bold text-gray-800 mb-2 flex items-center">
                  <div className="w-8 h-8 bg-[#0c7362]/10 rounded-lg flex items-center justify-center mr-3">
                    <i className="ri-table-line text-[#0c7362]"></i>
                  </div>
                  报送数据预览
                  <span className="ml-auto text-sm font-normal text-gray-500">
                    {reportPeriod} · {reportType}
                  </span>
                </h3>
                <p className="text-xs text-gray-400 mb-5 ml-11">
                  数据来源：快速录入模块实时同步，按品种自动汇总
                </p>

                {dataPreview.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-gray-300">
                    <div className="w-16 h-16 flex items-center justify-center rounded-full bg-gray-50 mb-3">
                      <i className="ri-inbox-line text-3xl"></i>
                    </div>
                    <p className="text-sm text-gray-400">暂无经营数据</p>
                    <p className="text-xs text-gray-300 mt-1">
                      请前往快速录入模块添加收购或销售记录
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-200">
                          <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                            粮食品种
                          </th>
                          <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">
                            期初库存(吨)
                          </th>
                          <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">
                            购进(吨)
                          </th>
                          <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">
                            销售(吨)
                          </th>
                          <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">
                            期末库存(吨)
                          </th>
                          <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                            粮食来源
                          </th>
                          <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                            粮食去向
                          </th>
                          <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">
                            操作
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {dataPreview.map((item, index) => (
                          <tr
                            key={index}
                            className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                          >
                            <td className="px-6 py-4 text-sm font-semibold text-gray-800">
                              {item.variety}
                            </td>
                            <td className="px-6 py-4 text-sm text-right text-gray-600">
                              {item.openingStock.toLocaleString()}
                            </td>
                            <td className="px-6 py-4 text-sm text-right text-emerald-600 font-medium">
                              +{item.purchase.toLocaleString()}
                            </td>
                            <td className="px-6 py-4 text-sm text-right text-rose-600 font-medium">
                              -{item.sales.toLocaleString()}
                            </td>
                            <td className="px-6 py-4 text-sm text-right text-gray-800 font-semibold">
                              {item.closingStock.toLocaleString()}
                            </td>
                            <td
                              className="px-6 py-4 text-xs text-gray-500 max-w-[160px] truncate"
                              title={item.purchaseSource}
                            >
                              {item.purchaseSource}
                            </td>
                            <td
                              className="px-6 py-4 text-xs text-gray-500 max-w-[160px] truncate"
                              title={item.salesTarget}
                            >
                              {item.salesTarget || '-'}
                            </td>
                            <td className="px-6 py-4 text-center">
                              <button
                                onClick={() => handleOpenDetail(item)}
                                className="text-[#0c7362] hover:text-[#085b4d] text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                              >
                                <i className="ri-edit-line mr-1"></i>详情
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-[#0c7362]/5 border-t-2 border-[#0c7362]/20">
                          <td className="px-6 py-4 text-sm font-bold text-gray-800">
                            合计
                          </td>
                          <td className="px-6 py-4 text-sm text-right font-bold text-gray-800">
                            {dataPreview
                              .reduce((s, i) => s + i.openingStock, 0)
                              .toLocaleString()}
                          </td>
                          <td className="px-6 py-4 text-sm text-right font-bold text-emerald-600">
                            +{dataPreview
                              .reduce((s, i) => s + i.purchase, 0)
                              .toLocaleString()}
                          </td>
                          <td className="px-6 py-4 text-sm text-right font-bold text-rose-600">
                            -{dataPreview
                              .reduce((s, i) => s + i.sales, 0)
                              .toLocaleString()}
                          </td>
                          <td className="px-6 py-4 text-sm text-right font-bold text-gray-800">
                            {dataPreview
                              .reduce((s, i) => s + i.closingStock, 0)
                              .toLocaleString()}
                          </td>
                          <td colSpan={3}></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>

              {/* 生成报送文件 */}
              <div className="bg-gradient-to-br from-[#0c7362]/5 to-[#0c7362]/10 rounded-2xl p-8 border border-[#0c7362]/20">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-bold text-gray-800 mb-2">
                      生成报送文件
                    </h3>
                    <p className="text-sm text-gray-600">
                      将生成符合{department}要求的标准Excel格式文件，共{' '}
                      {dataPreview.length} 个品种
                    </p>
                  </div>
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={() => {
                        if (dataPreview.length > 0) setPreviewModalOpen(true);
                      }}
                      disabled={dataPreview.length === 0}
                      className="px-6 py-3 bg-white text-gray-700 rounded-xl hover:bg-gray-50 transition-all border border-gray-200 font-medium whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <i className="ri-eye-line mr-2"></i>预览文件
                    </button>
                    <button
                      onClick={handleGenerate}
                      disabled={isGenerating || dataPreview.length === 0}
                      className="px-8 py-3 bg-gradient-to-r from-[#0c7362] to-[#085b4d] text-white rounded-xl hover:shadow-lg transition-all font-medium whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isGenerating ? (
                        <>
                          <i className="ri-loader-4-line mr-2 animate-spin"></i>
                          生成中...
                        </>
                      ) : (
                        <>
                          <i className="ri-download-cloud-line mr-2"></i>
                          生成并下载
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 报送历史 */}
          {activeTab === 'history' && (
            <div className="bg-white rounded-2xl border border-gray-100">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold text-gray-800 flex items-center">
                    <div className="w-8 h-8 bg-[#0c7362]/10 rounded-lg flex items-center justify-center mr-3">
                      <i className="ri-history-line text-[#0c7362]"></i>
                    </div>
                    报送历史记录
                  </h3>
                  <div className="flex items-center space-x-3">
                    <input
                      type="text"
                      placeholder="搜索报送记录..."
                      className="px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#0c7362] focus:border-transparent text-sm"
                    />
                    <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer">
                      <i className="ri-filter-3-line mr-2"></i>筛选
                    </button>
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                        报送日期
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                        报送类型
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                        报送周期
                      </th>
                      <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">
                        状态
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                        操作人
                      </th>
                      <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">
                        操作
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {historySubmissionRecords.map((record) => (
                      <tr
                        key={record.id}
                        className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-6 py-4 text-sm text-gray-800">
                          {record.date}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {record.type}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {record.period}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span
                            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                              record.status === '已报送'
                                ? 'bg-emerald-100 text-emerald-700'
                                : record.status === '待报送'
                                ? 'bg-amber-100 text-amber-700'
                                : 'bg-rose-100 text-rose-700'
                            }`}
                          >
                            <div
                              className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                                record.status === '已报送'
                                  ? 'bg-emerald-500'
                                  : record.status === '待报送'
                                  ? 'bg-amber-500'
                                  : 'bg-rose-500'
                              }`}
                            ></div>
                            {record.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {record.operator}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <div className="flex items-center justify-center space-x-2">
                            <button
                              onClick={() => handleOpenHistoryDetail(record)}
                              className="p-2 text-[#0c7362] hover:bg-[#0c7362]/10 rounded-lg transition-colors cursor-pointer"
                              title="查看详情"
                            >
                              <i className="ri-eye-line text-lg"></i>
                            </button>
                            <button
                              onClick={() => handleOpenHistoryDetail(record)}
                              className="p-2 text-[#0c7362] hover:bg-[#0c7362]/10 rounded-lg transition-colors cursor-pointer"
                              title="重新下载"
                            >
                              <i className="ri-download-line text-lg"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="p-6 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  共{' '}
                  <span className="font-semibold text-gray-800">
                    {historySubmissionRecords.length}
                  </span>{' '}
                  条记录
                </div>
                <div className="flex items-center space-x-2">
                  <button className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium whitespace-nowrap cursor-pointer">
                    上一页
                  </button>
                  <button className="px-4 py-2 bg-[#0c7362] text-white rounded-lg text-sm font-medium whitespace-nowrap cursor-pointer">
                    1
                  </button>
                  <button className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium whitespace-nowrap cursor-pointer">
                    下一页
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* 提醒设置 */}
          {activeTab === 'settings' && (
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-8 border border-gray-100">
                <h3 className="text-lg font-bold text-gray-800 mb-6 flex items-center">
                  <div className="w-8 h-8 bg-amber-500/10 rounded-lg flex items-center justify-center mr-3">
                    <i className="ri-notification-3-line text-amber-600"></i>
                  </div>
                  报送截止日期提醒
                </h3>
                <p className="text-sm text-gray-600 mb-6">
                  系统将在报送截止日期前的指定天数发送提醒通知
                </p>
                <div className="grid grid-cols-4 gap-4">
                  {[7, 5, 3, 1].map((day) => (
                    <button
                      key={day}
                      onClick={() => toggleReminder(day)}
                      className={`p-4 rounded-xl border-2 transition-all cursor-pointer ${
                        reminderDays.includes(day)
                          ? 'border-[#0c7362] bg-[#0c7362]/5'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div
                            className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              reminderDays.includes(day)
                                ? 'bg-[#0c7362] text-white'
                                : 'bg-gray-100 text-gray-400'
                            }`}
                          >
                            <i className="ri-alarm-line text-xl"></i>
                          </div>
                          <div className="text-left">
                            <div className="text-sm font-semibold text-gray-800">
                              提前 {day} 天
                            </div>
                            <div className="text-xs text-gray-500">
                              发送提醒
                            </div>
                          </div>
                        </div>
                        {reminderDays.includes(day) && (
                          <i className="ri-checkbox-circle-fill text-2xl text-[#0c7362]"></i>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="bg-gradient-to-br from-amber-500/10 to-amber-500/5 rounded-2xl p-8 border border-amber-500/20">
                <h3 className="text-lg font-bold text-gray-800 mb-6 flex items-center">
                  <div className="w-8 h-8 bg-amber-500/20 rounded-lg flex items-center justify-center mr-3">
                    <i className="ri-alarm-warning-line text-amber-600"></i>
                  </div>
                  即将到期的报送任务
                </h3>
                <div className="bg-white rounded-xl p-6 border border-gray-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-amber-500/10 rounded-xl flex items-center justify-center">
                        <i className="ri-calendar-event-line text-2xl text-amber-600"></i>
                      </div>
                      <div>
                        <div className="text-sm font-bold text-gray-800 mb-1">
                          2026年1月月度报送
                        </div>
                        <div className="text-xs text-gray-500">
                          截止日期：2026-02-15
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="text-right">
                        <div className="text-sm font-semibold text-amber-600">
                          剩余 7 天
                        </div>
                        <div className="text-xs text-gray-500">
                          请及时完成报送
                        </div>
                      </div>
                      <button
                        onClick={() => setActiveTab('new')}
                        className="px-6 py-2 bg-gradient-to-r from-[#0c7362] to-[#085b4d] text-white rounded-xl hover:shadow-md transition-all text-sm font-medium whitespace-nowrap cursor-pointer"
                      >
                        立即报送
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-8 border border-gray-100">
                <h3 className="text-lg font-bold text-gray-800 mb-6 flex items-center">
                  <div className="w-8 h-8 bg-[#0c7362]/10 rounded-lg flex items-center justify-center mr-3">
                    <i className="ri-message-3-line text-[#0c7362]"></i>
                  </div>
                  通知方式
                </h3>
                <div className="space-y-4">
                  {[
                    {
                      icon: 'ri-mail-line',
                      color: 'text-[#0c7362]',
                      bg: 'bg-[#0c7362]/10',
                      title: '邮件通知',
                      desc: '发送提醒邮件到注册邮箱',
                    },
                    {
                      icon: 'ri-notification-badge-line',
                      color: 'text-emerald-600',
                      bg: 'bg-emerald-500/10',
                      title: '系统通知',
                      desc: '在系统内显示通知消息',
                    },
                  ].map((item) => (
                    <div
                      key={item.title}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-xl"
                    >
                      <div className="flex items-center space-x-3">
                        <div
                          className={`w-10 h-10 ${item.bg} rounded-lg flex items-center justify-center`}
                        >
                          <i
                            className={`${item.icon} text-xl ${item.color}`}
                          ></i>
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-gray-800">
                            {item.title}
                          </div>
                          <div className="text-xs text-gray-500">
                            {item.desc}
                          </div>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          defaultChecked
                        />
                        <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#0c7362]"></div>
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* 接口配置 */}
              <div className="bg-white rounded-2xl p-8 border border-gray-100">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-bold text-gray-800 flex items-center">
                    <div className="w-8 h-8 bg-[#0c7362]/10 rounded-lg flex items-center justify-center mr-3">
                      <i className="ri-plug-line text-[#0c7362]"></i>
                    </div>
                    报送部门接口配置
                  </h3>
                  {configSaved && (
                    <span className="flex items-center text-sm text-emerald-600 bg-emerald-50 px-4 py-2 rounded-full font-medium">
                      <i className="ri-checkbox-circle-fill mr-1.5"></i>
                      配置已保存
                    </span>
                  )}
                </div>
                <div className="flex items-center bg-gray-100 rounded-xl p-1 mb-8 w-fit">
                  {[
                    { key: 'api', label: 'API 接口报送', icon: 'ri-server-line' },
                    { key: 'email', label: '邮件报送', icon: 'ri-mail-send-line' },
                  ].map((m) => (
                    <button
                      key={m.key}
                      onClick={() => setSubmitMode(m.key as 'api' | 'email')}
                      className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                        submitMode === m.key
                          ? 'bg-white text-[#0c7362] shadow-sm'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <i className={`${m.icon} mr-2`}></i>
                      {m.label}
                    </button>
                  ))}
                </div>

                {submitMode === 'api' && (
                  <div className="space-y-6">
                    <div className="flex items-start space-x-3 bg-[#0c7362]/5 border border-[#0c7362]/20 rounded-xl p-4">
                      <i className="ri-information-line text-[#0c7362] text-lg mt-0.5"></i>
                      <p className="text-sm text-[#0c7362]/80">
                        请向主管粮储部门获取数据报送接口地址和密钥信息，填写后可实现数据自动推送报送。
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-6">
                      {[
                        {
                          label: '服务器 IP / 域名',
                          icon: 'ri-global-line',
                          key: 'ip',
                          placeholder: '如：192.168.1.100 或 api.grain.gov.cn',
                          required: true,
                        },
                        {
                          label: '端口号',
                          icon: 'ri-door-open-line',
                          key: 'port',
                          placeholder: '如：8080 / 443 / 80',
                          required: true,
                        },
                        {
                          label: '接口路径',
                          icon: 'ri-route-line',
                          key: 'path',
                          placeholder: '如：/api/grain/submit',
                          required: false,
                        },
                      ].map((field) => (
                        <div key={field.key}>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            <i
                              className={`${field.icon} text-[#0c7362] mr-1`}
                            ></i>
                            {field.label}
                            {field.required && (
                              <span className="text-rose-500 ml-1">*</span>
                            )}
                          </label>
                          <input
                            type="text"
                            value={apiConfig[
                              field.key as keyof typeof apiConfig
                            ]}
                            onChange={(e) =>
                              setApiConfig({
                                ...apiConfig,
                                [field.key]: e.target.value,
                              })
                            }
                            placeholder={field.placeholder}
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#0c7362] focus:border-transparent text-sm"
                          />
                        </div>
                      ))}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <i className="ri-shield-keyhole-line text-[#0c7362] mr-1"></i>
                          通信协议
                        </label>
                        <select
                          value={apiConfig.protocol}
                          onChange={(e) =>
                            setApiConfig({
                              ...apiConfig,
                              protocol: e.target.value,
                            })
                          }
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#0c7362] focus:border-transparent text-sm cursor-pointer"
                        >
                          <option>HTTPS</option>
                          <option>HTTP</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <i className="ri-key-2-line text-[#0c7362] mr-1"></i>
                        接口密钥（Secret Key）
                        <span className="text-rose-500 ml-1">*</span>
                      </label>
                      <input
                        type="password"
                        value={apiConfig.secretKey}
                        onChange={(e) =>
                          setApiConfig({
                            ...apiConfig,
                            secretKey: e.target.value,
                          })
                        }
                        placeholder="请输入部门提供的接口密钥"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#0c7362] focus:border-transparent text-sm font-mono"
                      />
                    </div>
                    <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                      <div className="text-xs text-gray-500 mb-2 font-medium">
                        接口地址预览
                      </div>
                      <div className="font-mono text-sm text-gray-700 break-all">
                        <span className="text-[#0c7362] font-semibold">
                          {apiConfig.protocol.toLowerCase()}://
                        </span>
                        <span>{apiConfig.ip}</span>
                        {apiConfig.port && <span>:{apiConfig.port}</span>}
                        <span className="text-gray-500">{apiConfig.path}</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between pt-2">
                      <button
                        onClick={handleTestApi}
                        disabled={apiTestStatus === 'testing'}
                        className="flex items-center px-6 py-3 border-2 border-[#0c7362] text-[#0c7362] rounded-xl hover:bg-[#0c7362]/5 transition-all font-medium text-sm whitespace-nowrap cursor-pointer disabled:opacity-60"
                      >
                        {apiTestStatus === 'testing' ? (
                          <>
                            <i className="ri-loader-4-line mr-2 animate-spin"></i>
                            连接测试中...
                          </>
                        ) : apiTestStatus === 'success' ? (
                          <>
                            <i className="ri-checkbox-circle-fill mr-2 text-emerald-500"></i>
                            <span className="text-emerald-600">连接成功！</span>
                          </>
                        ) : apiTestStatus === 'fail' ? (
                          <>
                            <i className="ri-close-circle-fill mr-2 text-rose-500"></i>
                            <span className="text-rose-600">连接失败</span>
                          </>
                        ) : (
                          <>
                            <i className="ri-wifi-line mr-2"></i>测试连接
                          </>
                        )}
                      </button>
                      <button
                        onClick={handleSaveConfig}
                        className="px-8 py-3 bg-gradient-to-r from-[#0c7362] to-[#085b4d] text-white rounded-xl hover:shadow-lg transition-all font-medium text-sm whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-save-line mr-2"></i>保存配置
                      </button>
                    </div>
                  </div>
                )}

                {submitMode === 'email' && (
                  <div className="space-y-6">
                    <div className="flex items-start space-x-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
                      <i className="ri-information-line text-amber-500 text-lg mt-0.5"></i>
                      <p className="text-sm text-amber-700">
                        系统将在您提交报送时，自动将台账数据文件以附件形式发送至指定邮箱。请确认收件邮箱地址正确。
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-6">
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <i className="ri-mail-line text-[#0c7362] mr-1"></i>
                          收件邮箱（报送目标部门）
                          <span className="text-rose-500 ml-1">*</span>
                        </label>
                        <input
                          type="email"
                          value={emailConfig.toEmail}
                          onChange={(e) =>
                            setEmailConfig({
                              ...emailConfig,
                              toEmail: e.target.value,
                            })
                          }
                          placeholder="如：grain-report@province.gov.cn"
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#0c7362] focus:border-transparent text-sm"
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <i className="ri-mail-add-line text-[#0c7362] mr-1"></i>
                          抄送邮箱（可选）
                        </label>
                        <input
                          type="email"
                          value={emailConfig.ccEmail}
                          onChange={(e) =>
                            setEmailConfig({
                              ...emailConfig,
                              ccEmail: e.target.value,
                            })
                          }
                          placeholder="如：manager@yourcompany.com"
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#0c7362] focus:border-transparent text-sm"
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <i className="ri-text-snippet-line text-[#0c7362] mr-1"></i>
                          邮件主题模板
                        </label>
                        <input
                          type="text"
                          value={emailConfig.subject}
                          onChange={(e) =>
                            setEmailConfig({
                              ...emailConfig,
                              subject: e.target.value,
                            })
                          }
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#0c7362] focus:border-transparent text-sm"
                        />
                      </div>
                    </div>
                    <div className="flex items-center justify-between pt-2">
                      <button
                        onClick={handleTestEmail}
                        disabled={emailTestStatus === 'testing'}
                        className="flex items-center px-6 py-3 border-2 border-[#0c7362] text-[#0c7362] rounded-xl hover:bg-[#0c7362]/5 transition-all font-medium text-sm whitespace-nowrap cursor-pointer disabled:opacity-60"
                      >
                        {emailTestStatus === 'testing' ? (
                          <>
                            <i className="ri-loader-4-line mr-2 animate-spin"></i>
                            发送测试中...
                          </>
                        ) : emailTestStatus === 'success' ? (
                          <>
                            <i className="ri-checkbox-circle-fill mr-2 text-emerald-500"></i>
                            <span className="text-emerald-600">测试邮件已发送！</span>
                          </>
                        ) : (
                          <>
                            <i className="ri-send-plane-line mr-2"></i>发送测试邮件
                          </>
                        )}
                      </button>
                      <button
                        onClick={handleSaveConfig}
                        className="px-8 py-3 bg-gradient-to-r from-[#0c7362] to-[#085b4d] text-white rounded-xl hover:shadow-lg transition-all font-medium text-sm whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-save-line mr-2"></i>保存配置
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 报送数据详情弹窗 — 关联真实数据 */}
          {detailModalOpen && selectedItem && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
              onClick={() => setDetailModalOpen(false)}
            >
              <div
                className="bg-white rounded-2xl shadow-2xl w-[900px] max-h-[90vh] overflow-y-auto relative"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="sticky top-0 bg-white z-10 px-8 pt-7 pb-5 border-b border-gray-200 rounded-t-2xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-[#0c7362]/10 rounded-xl flex items-center justify-center">
                        <i className="ri-file-info-line text-xl text-[#0c7362]"></i>
                      </div>
                      <div>
                        <h2 className="text-lg font-bold text-gray-800">
                          报送数据详情 — {selectedItem.variety}
                        </h2>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {reportPeriod} · {reportType} · 仅备注信息可修改
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setDetailModalOpen(false)}
                      className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer text-gray-400"
                    >
                      <i className="ri-close-line text-xl"></i>
                    </button>
                  </div>
                  {remarkSaved && (
                    <div className="mt-3 flex items-center text-sm text-emerald-600 bg-emerald-50 px-4 py-2 rounded-lg font-medium">
                      <i className="ri-checkbox-circle-fill mr-1.5"></i>
                      备注信息已保存
                    </div>
                  )}
                </div>

                <div className="px-8 py-6 space-y-6">
                  {/* 经营主体信息 */}
                  <div>
                    <h4 className="text-sm font-bold text-gray-800 mb-3 flex items-center">
                      <div className="w-6 h-6 bg-[#0c7362]/10 rounded-md flex items-center justify-center mr-2">
                        <i className="ri-building-line text-[#0c7362] text-xs"></i>
                      </div>
                      经营主体信息
                    </h4>
                    <div className="bg-gray-50 rounded-xl p-5 grid grid-cols-2 gap-x-8 gap-y-3">
                      {[
                        ['企业名称', '丰收粮食贸易有限公司'],
                        ['统一社会信用代码', '91410100MA47XXXXXX'],
                        ['法定代表人', '张建国'],
                        ['联系电话', '0371-6688XXXX'],
                      ].map(([k, v]) => (
                        <div key={k} className="flex items-start">
                          <span className="text-xs text-gray-400 w-28 shrink-0 pt-0.5">
                            {k}
                          </span>
                          <span className="text-sm text-gray-800 font-medium">
                            {v}
                          </span>
                        </div>
                      ))}
                      <div className="flex items-start col-span-2">
                        <span className="text-xs text-gray-400 w-28 shrink-0 pt-0.5">
                          经营地址
                        </span>
                        <span className="text-sm text-gray-800">
                          河南省郑州市中原区粮食产业园A栋
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* 购进信息 — 真实数据 */}
                  <div>
                    <h4 className="text-sm font-bold text-gray-800 mb-3 flex items-center">
                      <div className="w-6 h-6 bg-emerald-500/10 rounded-md flex items-center justify-center mr-2">
                        <i className="ri-shopping-cart-line text-emerald-600 text-xs"></i>
                      </div>
                      购进信息
                      <span className="ml-2 text-xs font-normal text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                        共 {selectedItem.purchaseRecords.length} 笔
                      </span>
                    </h4>
                    <div className="bg-gray-50 rounded-xl p-5 space-y-3">
                      <div className="grid grid-cols-2 gap-x-8 gap-y-3 mb-3">
                        {[
                          ['粮食品种', selectedItem.variety],
                          ['粮食性质', selectedItem.grainNature],
                          ['购进总量', `${selectedItem.purchase.toLocaleString()} 吨`],
                          ['最近购进日期', selectedItem.purchaseDate],
                          ['参考单价', selectedItem.purchasePrice],
                          ['结算方式', selectedItem.settlementMethod],
                        ].map(([k, v]) => (
                          <div key={k} className="flex items-start">
                            <span className="text-xs text-gray-400 w-28 shrink-0 pt-0.5">
                              {k}
                            </span>
                            <span className="text-sm text-gray-800 font-medium">
                              {v}
                            </span>
                          </div>
                        ))}
                        <div className="flex items-start col-span-2">
                          <span className="text-xs text-gray-400 w-28 shrink-0 pt-0.5">
                            粮食来源
                          </span>
                          <span className="text-sm text-gray-800">
                            {selectedItem.purchaseSource}
                          </span>
                        </div>
                      </div>
                      {selectedItem.purchaseRecords.length > 0 && (
                        <div>
                          <div className="text-xs text-gray-400 font-medium mb-2">
                            购进明细
                          </div>
                          <div className="space-y-1.5">
                            {selectedItem.purchaseRecords.map((r, i) => (
                              <div
                                key={i}
                                className="flex items-center justify-between bg-white rounded-lg px-4 py-2.5 border border-gray-100"
                              >
                                <span className="text-xs text-gray-500">
                                  {r.date}
                                </span>
                                <span className="text-xs font-medium text-gray-700">
                                  {r.counterpart || '未知来源'}
                                </span>
                                <span className="text-xs text-emerald-600 font-semibold">
                                  ¥{r.amount.toLocaleString()}
                                </span>
                                <span className="text-xs text-gray-400">
                                  {r.settlement}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* 储存信息 */}
                  <div>
                    <h4 className="text-sm font-bold text-gray-800 mb-3 flex items-center">
                      <div className="w-6 h-6 bg-amber-500/10 rounded-md flex items-center justify-center mr-2">
                        <i className="ri-home-4-line text-amber-600 text-xs"></i>
                      </div>
                      储存信息
                    </h4>
                    <div className="bg-gray-50 rounded-xl p-5 grid grid-cols-2 gap-x-8 gap-y-3">
                      {[
                        ['期初库存', `${selectedItem.openingStock.toLocaleString()} 吨`],
                        ['期末库存', `${selectedItem.closingStock.toLocaleString()} 吨`],
                        ['储存地点', selectedItem.storageLocation],
                        ['储存方式', selectedItem.storageMethod],
                        ['储存品质', selectedItem.storageQuality],
                      ].map(([k, v]) => (
                        <div key={k} className="flex items-start">
                          <span className="text-xs text-gray-400 w-28 shrink-0 pt-0.5">
                            {k}
                          </span>
                          <span className="text-sm text-gray-800">{v}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* 销售信息 — 真实数据 */}
                  <div>
                    <h4 className="text-sm font-bold text-gray-800 mb-3 flex items-center">
                      <div className="w-6 h-6 bg-rose-500/10 rounded-md flex items-center justify-center mr-2">
                        <i className="ri-truck-line text-rose-600 text-xs"></i>
                      </div>
                      销售信息
                      <span className="ml-2 text-xs font-normal text-rose-600 bg-rose-50 px-2 py-0.5 rounded-full">
                        共 {selectedItem.salesRecords.length} 笔
                      </span>
                    </h4>
                    <div className="bg-gray-50 rounded-xl p-5 space-y-3">
                      <div className="grid grid-cols-2 gap-x-8 gap-y-3 mb-3">
                        {[
                          ['销售总量', `${selectedItem.sales.toLocaleString()} 吨`],
                          ['最近销售日期', selectedItem.salesDate],
                          ['参考单价', selectedItem.salesPrice],
                          ['结算方式', selectedItem.settlementMethod],
                        ].map(([k, v]) => (
                          <div key={k} className="flex items-start">
                            <span className="text-xs text-gray-400 w-28 shrink-0 pt-0.5">
                              {k}
                            </span>
                            <span className="text-sm text-gray-800 font-medium">
                              {v}
                            </span>
                          </div>
                        ))}
                        <div className="flex items-start col-span-2">
                          <span className="text-xs text-gray-400 w-28 shrink-0 pt-0.5">
                            粮食去向
                          </span>
                          <span className="text-sm text-gray-800">
                            {selectedItem.salesTarget || '-'}
                          </span>
                        </div>
                      </div>
                      {selectedItem.salesRecords.length > 0 && (
                        <div>
                          <div className="text-xs text-gray-400 font-medium mb-2">
                            销售明细
                          </div>
                          <div className="space-y-1.5">
                            {selectedItem.salesRecords.map((r, i) => (
                              <div
                                key={i}
                                className="flex items-center justify-between bg-white rounded-lg px-4 py-2.5 border border-gray-100"
                              >
                                <span className="text-xs text-gray-500">
                                  {r.date}
                                </span>
                                <span className="text-xs font-medium text-gray-700">
                                  {r.counterpart || '未知客户'}
                                </span>
                                <span className="text-xs text-rose-600 font-semibold">
                                  ¥{r.amount.toLocaleString()}
                                </span>
                                <span className="text-xs text-gray-400">
                                  {r.settlement}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* 质量指标 */}
                  <div>
                    <h4 className="text-sm font-bold text-gray-800 mb-3 flex items-center">
                      <div className="w-6 h-6 bg-[#0c7362]/10 rounded-md flex items-center justify-center mr-2">
                        <i className="ri-test-tube-line text-[#0c7362] text-xs"></i>
                      </div>
                      质量指标
                    </h4>
                    <div className="bg-gray-50 rounded-xl p-5 grid grid-cols-3 gap-x-8 gap-y-3">
                      {[
                        ['质量等级', selectedItem.qualityGrade],
                        ['水分含量', selectedItem.moistureContent],
                        ['杂质率', selectedItem.impurityRate],
                        ['不完善粒', selectedItem.unsoundKernel],
                        ['食品安全', selectedItem.foodSafety],
                      ].map(([k, v]) => (
                        <div key={k} className="flex items-start">
                          <span className="text-xs text-gray-400 w-20 shrink-0 pt-0.5">
                            {k}
                          </span>
                          <span className="text-sm text-gray-800">{v}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* 备注（可编辑） */}
                  <div>
                    <h4 className="text-sm font-bold text-gray-800 mb-3 flex items-center">
                      <div className="w-6 h-6 bg-amber-500/10 rounded-md flex items-center justify-center mr-2">
                        <i className="ri-edit-2-line text-amber-600 text-xs"></i>
                      </div>
                      备注信息
                      <span className="ml-2 text-xs font-normal text-[#0c7362] bg-[#0c7362]/10 px-2 py-0.5 rounded-full">
                        可修改
                      </span>
                    </h4>
                    <div className="bg-[#0c7362]/5 rounded-xl p-5 border border-[#0c7362]/15">
                      <textarea
                        value={editingRemark}
                        onChange={(e) => {
                          if (e.target.value.length <= 500)
                            setEditingRemark(e.target.value);
                        }}
                        placeholder="请输入备注信息，如特殊情况说明、补充事项等..."
                        rows={4}
                        maxLength={500}
                        className="w-full px-4 py-3 border border-[#0c7362]/20 rounded-xl focus:ring-2 focus:ring-[#0c7362] focus:border-transparent text-sm bg-white resize-none"
                      />
                      <div className="flex items-center justify-between mt-2">
                        <p className="text-xs text-gray-400">
                          <i className="ri-information-line mr-1"></i>
                          备注信息用于补充说明本品种报送数据的特殊情况
                        </p>
                        <span
                          className={`text-xs ${
                            editingRemark.length >= 450
                              ? 'text-amber-500'
                              : 'text-gray-400'
                          }`}
                        >
                          {editingRemark.length}/500
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="sticky bottom-0 bg-white px-8 py-5 border-t border-gray-200 rounded-b-2xl flex items-center justify-end space-x-4">
                  <button
                    onClick={() => setDetailModalOpen(false)}
                    className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 text-sm font-medium whitespace-nowrap cursor-pointer"
                  >
                    取消
                  </button>
                  <button
                    onClick={handleSaveRemark}
                    className="px-8 py-2.5 bg-gradient-to-r from-[#0c7362] to-[#085b4d] text-white rounded-xl hover:shadow-lg text-sm font-medium whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-save-line mr-2"></i>保存备注
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* 预览文件弹窗 */}
          {previewModalOpen && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
              onClick={() => setPreviewModalOpen(false)}
            >
              <div
                className="bg-white rounded-2xl shadow-2xl w-[1000px] max-h-[92vh] overflow-hidden relative flex flex-col"
                onClick={(e) => e.stopPropagation()}
              >
                {/* 弹窗头部 */}
                <div className="sticky top-0 bg-white z-10 px-8 pt-6 pb-4 border-b border-gray-200 rounded-t-2xl flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-[#0c7362]/10 rounded-xl flex items-center justify-center">
                      <i className="ri-file-text-line text-xl text-[#0c7362]"></i>
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-gray-800">报送文件预览</h2>
                      <p className="text-xs text-gray-500 mt-0.5">预览生成的标准报送文件内容</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={handleExportPreviewPDF}
                      disabled={exportingPDF}
                      className="px-4 py-2 bg-gradient-to-r from-[#0c7362] to-[#085b4d] text-white rounded-lg hover:shadow-md transition-all text-sm font-medium whitespace-nowrap cursor-pointer disabled:opacity-60"
                    >
                      {exportingPDF ? (
                        <>
                          <i className="ri-loader-4-line mr-1.5 animate-spin"></i>导出中...
                        </>
                      ) : (
                        <>
                          <i className="ri-file-pdf-2-line mr-1.5"></i>导出PDF
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => window.print()}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-printer-line mr-1.5"></i>打印
                    </button>
                    <button
                      onClick={() => setPreviewModalOpen(false)}
                      className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer text-gray-400"
                    >
                      <i className="ri-close-line text-xl"></i>
                    </button>
                  </div>
                </div>

                {/* 文件内容 */}
                <div className="flex-1 overflow-y-auto px-8 py-6">
                  <div ref={previewContentRef} className="bg-white border border-gray-300 rounded-lg shadow-sm mx-auto max-w-[900px]" style={{ fontFamily: 'SimSun, serif' }}>
                    {/* 文件标题 */}
                    <div className="border-b-2 border-gray-800 px-10 pt-10 pb-6 text-center">
                      <div className="text-xs text-gray-500 mb-3 tracking-widest">密级：内部</div>
                      <h1 className="text-2xl font-bold text-gray-900 tracking-wider mb-2">粮食经营台账数据报送表</h1>
                      <div className="flex items-center justify-center space-x-6 text-sm text-gray-600 mt-4">
                        <span>报送类型：<strong className="text-gray-800">{reportType}</strong></span>
                        <span className="text-gray-300">|</span>
                        <span>报送周期：<strong className="text-gray-800">{reportPeriod}</strong></span>
                        <span className="text-gray-300">|</span>
                        <span>报送部门：<strong className="text-gray-800">{department}</strong></span>
                      </div>
                    </div>

                    <div className="px-10 py-6 space-y-6">
                      {/* 一、经营主体基本信息 */}
                      <div>
                        <h3 className="text-base font-bold text-gray-800 mb-3 flex items-center">
                          <span className="w-6 h-6 bg-gray-800 text-white rounded text-xs flex items-center justify-center mr-2 font-bold">一</span>
                          经营主体基本信息
                        </h3>
                        <table className="w-full border-collapse text-sm">
                          <tbody>
                            <tr>
                              <td className="border border-gray-400 bg-gray-50 px-3 py-2 text-gray-600 w-[130px] font-medium">企业名称</td>
                              <td className="border border-gray-400 px-3 py-2 text-gray-800">丰收粮食贸易有限公司</td>
                              <td className="border border-gray-400 bg-gray-50 px-3 py-2 text-gray-600 font-medium">统一社会信用代码</td>
                              <td className="border border-gray-400 px-3 py-2 text-gray-800">91410100MA47XXXXXX</td>
                            </tr>
                            <tr>
                              <td className="border border-gray-400 bg-gray-50 px-3 py-2 text-gray-600 font-medium">法定代表人</td>
                              <td className="border border-gray-400 px-3 py-2 text-gray-800">张建国</td>
                              <td className="border border-gray-400 bg-gray-50 px-3 py-2 text-gray-600 font-medium">联系电话</td>
                              <td className="border border-gray-400 px-3 py-2 text-gray-800">0371-6688XXXX</td>
                            </tr>
                            <tr>
                              <td className="border border-gray-400 bg-gray-50 px-3 py-2 text-gray-600 font-medium">经营地址</td>
                              <td className="border border-gray-400 px-3 py-2 text-gray-800" colSpan={3}>河南省郑州市中原区粮食产业园A栋</td>
                            </tr>
                            <tr>
                              <td className="border border-gray-400 bg-gray-50 px-3 py-2 text-gray-600 font-medium">经营品种数</td>
                              <td className="border border-gray-400 px-3 py-2 text-gray-800">{stats.totalVarieties} 种</td>
                              <td className="border border-gray-400 bg-gray-50 px-3 py-2 text-gray-600 font-medium">填报日期</td>
                              <td className="border border-gray-400 px-3 py-2 text-gray-800">{new Date().toLocaleDateString('zh-CN')}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      {/* 二、粮食购进、销售、储存汇总 */}
                      <div>
                        <h3 className="text-base font-bold text-gray-800 mb-3 flex items-center">
                          <span className="w-6 h-6 bg-gray-800 text-white rounded text-xs flex items-center justify-center mr-2 font-bold">二</span>
                          粮食购进、销售、储存汇总表
                        </h3>
                        <div className="overflow-x-auto">
                          <table className="w-full border-collapse text-xs">
                            <thead>
                              <tr className="bg-gray-100">
                                <th className="border border-gray-400 px-2 py-2 text-gray-700 font-bold" rowSpan={2}>粮食品种</th>
                                <th className="border border-gray-400 px-2 py-2 text-gray-700 font-bold" rowSpan={2}>粮食性质</th>
                                <th className="border border-gray-400 px-2 py-2 text-gray-700 font-bold" colSpan={2}>库存（吨）</th>
                                <th className="border border-gray-400 px-2 py-2 text-gray-700 font-bold" colSpan={3}>购进</th>
                                <th className="border border-gray-400 px-2 py-2 text-gray-700 font-bold" colSpan={3}>销售</th>
                                <th className="border border-gray-400 px-2 py-2 text-gray-700 font-bold" rowSpan={2}>储存地点</th>
                              </tr>
                              <tr className="bg-gray-50">
                                <th className="border border-gray-400 px-2 py-1.5 text-gray-600 font-medium">期初</th>
                                <th className="border border-gray-400 px-2 py-1.5 text-gray-600 font-medium">期末</th>
                                <th className="border border-gray-400 px-2 py-1.5 text-gray-600 font-medium">数量(吨)</th>
                                <th className="border border-gray-400 px-2 py-1.5 text-gray-600 font-medium">单价(元/吨)</th>
                                <th className="border border-gray-400 px-2 py-1.5 text-gray-600 font-medium">来源</th>
                                <th className="border border-gray-400 px-2 py-1.5 text-gray-600 font-medium">数量(吨)</th>
                                <th className="border border-gray-400 px-2 py-1.5 text-gray-600 font-medium">单价(元/吨)</th>
                                <th className="border border-gray-400 px-2 py-1.5 text-gray-600 font-medium">去向</th>
                              </tr>
                            </thead>
                            <tbody>
                              {dataPreview.map((item, idx) => (
                                <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                                  <td className="border border-gray-400 px-2 py-2 text-center font-medium text-gray-800">{item.variety}</td>
                                  <td className="border border-gray-400 px-2 py-2 text-center text-gray-600">{item.grainNature}</td>
                                  <td className="border border-gray-400 px-2 py-2 text-right text-gray-700">{item.openingStock.toLocaleString()}</td>
                                  <td className="border border-gray-400 px-2 py-2 text-right text-gray-700">{item.closingStock.toLocaleString()}</td>
                                  <td className="border border-gray-400 px-2 py-2 text-right text-emerald-700 font-medium">{item.purchase.toLocaleString()}</td>
                                  <td className="border border-gray-400 px-2 py-2 text-right text-gray-700">{item.purchasePrice}</td>
                                  <td className="border border-gray-400 px-2 py-2 text-gray-600 max-w-[100px] truncate" title={item.purchaseSource}>{item.purchaseSource}</td>
                                  <td className="border border-gray-400 px-2 py-2 text-right text-rose-700 font-medium">{item.sales.toLocaleString()}</td>
                                  <td className="border border-gray-400 px-2 py-2 text-right text-gray-700">{item.salesPrice}</td>
                                  <td className="border border-gray-400 px-2 py-2 text-gray-600 max-w-[100px] truncate" title={item.salesTarget}>{item.salesTarget || '-'}</td>
                                  <td className="border border-gray-400 px-2 py-2 text-center text-gray-600">—</td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr className="bg-gray-100 font-bold">
                                <td className="border border-gray-400 px-2 py-2 text-center text-gray-800">合计</td>
                                <td className="border border-gray-400 px-2 py-2 text-center text-gray-600">—</td>
                                <td className="border border-gray-400 px-2 py-2 text-right text-gray-800">{dataPreview.reduce((s, i) => s + i.openingStock, 0).toLocaleString()}</td>
                                <td className="border border-gray-400 px-2 py-2 text-right text-gray-800">{dataPreview.reduce((s, i) => s + i.closingStock, 0).toLocaleString()}</td>
                                <td className="border border-gray-400 px-2 py-2 text-right text-emerald-700">{dataPreview.reduce((s, i) => s + i.purchase, 0).toLocaleString()}</td>
                                <td className="border border-gray-400 px-2 py-2 text-center text-gray-600">—</td>
                                <td className="border border-gray-400 px-2 py-2 text-center text-gray-600">—</td>
                                <td className="border border-gray-400 px-2 py-2 text-right text-rose-700">{dataPreview.reduce((s, i) => s + i.sales, 0).toLocaleString()}</td>
                                <td className="border border-gray-400 px-2 py-2 text-center text-gray-600">—</td>
                                <td className="border border-gray-400 px-2 py-2 text-center text-gray-600">—</td>
                                <td className="border border-gray-400 px-2 py-2 text-center text-gray-600">—</td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </div>

                      {/* 三、质量指标 */}
                      <div>
                        <h3 className="text-base font-bold text-gray-800 mb-3 flex items-center">
                          <span className="w-6 h-6 bg-gray-800 text-white rounded text-xs flex items-center justify-center mr-2 font-bold">三</span>
                          粮食质量指标
                        </h3>
                        <table className="w-full border-collapse text-xs">
                          <thead>
                            <tr className="bg-gray-100">
                              <th className="border border-gray-400 px-3 py-2 text-gray-700 font-bold">粮食品种</th>
                              <th className="border border-gray-400 px-3 py-2 text-gray-700 font-bold">质量等级</th>
                              <th className="border border-gray-400 px-3 py-2 text-gray-700 font-bold">水分含量</th>
                              <th className="border border-gray-400 px-3 py-2 text-gray-700 font-bold">杂质率</th>
                              <th className="border border-gray-400 px-3 py-2 text-gray-700 font-bold">不完善粒</th>
                              <th className="border border-gray-400 px-3 py-2 text-gray-700 font-bold">储存品质</th>
                              <th className="border border-gray-400 px-3 py-2 text-gray-700 font-bold">食品安全</th>
                            </tr>
                          </thead>
                          <tbody>
                            {dataPreview.map((item, idx) => (
                              <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                                <td className="border border-gray-400 px-3 py-2 text-center font-medium text-gray-800">{item.variety}</td>
                                <td className="border border-gray-400 px-3 py-2 text-center text-gray-700">{item.qualityGrade}</td>
                                <td className="border border-gray-400 px-3 py-2 text-center text-gray-700">{item.moistureContent}</td>
                                <td className="border border-gray-400 px-3 py-2 text-center text-gray-700">{item.impurityRate}</td>
                                <td className="border border-gray-400 px-3 py-2 text-center text-gray-700">{item.unsoundKernel}</td>
                                <td className="border border-gray-400 px-3 py-2 text-center text-gray-700">{item.storageQuality}</td>
                                <td className="border border-gray-400 px-3 py-2 text-center text-gray-700">{item.foodSafety}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* 四、财务信息 */}
                      <div>
                        <h3 className="text-base font-bold text-gray-800 mb-3 flex items-center">
                          <span className="w-6 h-6 bg-gray-800 text-white rounded text-xs flex items-center justify-center mr-2 font-bold">四</span>
                          粮食经营财务信息
                        </h3>
                        <table className="w-full border-collapse text-xs">
                          <thead>
                            <tr className="bg-gray-100">
                              <th className="border border-gray-400 px-3 py-2 text-gray-700 font-bold">粮食品种</th>
                              <th className="border border-gray-400 px-3 py-2 text-gray-700 font-bold">收购金额(元)</th>
                              <th className="border border-gray-400 px-3 py-2 text-gray-700 font-bold">营业收入(元)</th>
                              <th className="border border-gray-400 px-3 py-2 text-gray-700 font-bold">营业成本(元)</th>
                              <th className="border border-gray-400 px-3 py-2 text-gray-700 font-bold">结算方式</th>
                              <th className="border border-gray-400 px-3 py-2 text-gray-700 font-bold">应缴税款(元)</th>
                              <th className="border border-gray-400 px-3 py-2 text-gray-700 font-bold">政府补贴(元)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {dataPreview.map((item, idx) => {
                              const purchaseAmount = item.purchaseRecords.reduce((s, r) => s + r.amount, 0);
                              const salesAmount = item.salesRecords.reduce((s, r) => s + r.amount, 0);
                              const costAmount = item.purchaseRecords.reduce((s, r) => s + r.amount, 0);
                              const taxAmount = financeRecords
                                .filter(r => r.variety === item.variety && r.type === '销售')
                                .reduce((s, r) => s + (r.taxAmount || 0), 0);
                              const subsidyAmount = financeRecords
                                .filter(r => r.variety === item.variety)
                                .reduce((s, r) => s + (r.subsidyAmount || 0), 0);
                              return (
                                <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                                  <td className="border border-gray-400 px-3 py-2 text-center font-medium text-gray-800">{item.variety}</td>
                                  <td className="border border-gray-400 px-3 py-2 text-right text-gray-700">{purchaseAmount.toLocaleString()}</td>
                                  <td className="border border-gray-400 px-3 py-2 text-right text-emerald-700 font-medium">{salesAmount.toLocaleString()}</td>
                                  <td className="border border-gray-400 px-3 py-2 text-right text-gray-700">{costAmount.toLocaleString()}</td>
                                  <td className="border border-gray-400 px-3 py-2 text-center text-gray-700">{item.settlementMethod}</td>
                                  <td className="border border-gray-400 px-3 py-2 text-right text-amber-700">{taxAmount.toLocaleString()}</td>
                                  <td className="border border-gray-400 px-3 py-2 text-right text-[#0c7362]">{subsidyAmount.toLocaleString()}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                          <tfoot>
                            <tr className="bg-gray-100 font-bold">
                              <td className="border border-gray-400 px-3 py-2 text-center text-gray-800">合计</td>
                              <td className="border border-gray-400 px-3 py-2 text-right text-gray-800">
                                {dataPreview.reduce((s, item) => s + item.purchaseRecords.reduce((a, r) => a + r.amount, 0), 0).toLocaleString()}
                              </td>
                              <td className="border border-gray-400 px-3 py-2 text-right text-emerald-700">
                                {dataPreview.reduce((s, item) => s + item.salesRecords.reduce((a, r) => a + r.amount, 0), 0).toLocaleString()}
                              </td>
                              <td className="border border-gray-400 px-3 py-2 text-right text-gray-800">
                                {dataPreview.reduce((s, item) => s + item.purchaseRecords.reduce((a, r) => a + r.amount, 0), 0).toLocaleString()}
                              </td>
                              <td className="border border-gray-400 px-3 py-2 text-center text-gray-600">—</td>
                              <td className="border border-gray-400 px-3 py-2 text-right text-amber-700">
                                {financeRecords.filter(r => r.type === '销售').reduce((s, r) => s + (r.taxAmount || 0), 0).toLocaleString()}
                              </td>
                              <td className="border border-gray-400 px-3 py-2 text-right text-[#0c7362]">
                                {financeRecords.reduce((s, r) => s + (r.subsidyAmount || 0), 0).toLocaleString()}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>

                      {/* 备注 */}
                      {dataPreview.some(item => item.remark) && (
                        <div>
                          <h3 className="text-base font-bold text-gray-800 mb-3 flex items-center">
                            <span className="w-6 h-6 bg-gray-800 text-white rounded text-xs flex items-center justify-center mr-2 font-bold">五</span>
                            备注说明
                          </h3>
                          <div className="border border-gray-400 rounded px-4 py-3 text-sm text-gray-700 space-y-1">
                            {dataPreview.filter(item => item.remark).map((item, idx) => (
                              <div key={idx}>
                                <span className="font-medium text-gray-800">{item.variety}：</span>
                                {item.remark}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* 保密声明 */}
                      <div className="bg-amber-50 border border-amber-300 rounded-lg px-4 py-3 text-xs text-amber-800 leading-relaxed">
                        <i className="ri-shield-keyhole-line mr-1"></i>
                        <strong>保密声明：</strong>本报送数据涉及商业秘密和个人隐私，严格按照《中华人民共和国保守国家秘密法》及相关规定执行。报送的基本数据和有关情况须与生产经营实际相符，与粮食经营台账相符。未经授权，不得向第三方披露。
                      </div>

                      {/* 签章区域 */}
                      <div className="border-t-2 border-gray-800 pt-6 mt-6">
                        <div className="grid grid-cols-3 gap-8 text-sm text-gray-700">
                          <div>
                            <div className="font-medium text-gray-800 mb-6">填报人（签字）：</div>
                            <div className="border-b border-gray-400 pb-1 mb-4"></div>
                          </div>
                          <div>
                            <div className="font-medium text-gray-800 mb-6">企业负责人（签字）：</div>
                            <div className="border-b border-gray-400 pb-1 mb-4"></div>
                          </div>
                          <div>
                            <div className="font-medium text-gray-800 mb-6">（企业公章）</div>
                            <div className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                              <span className="text-xs text-gray-400">公章位置</span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right text-sm text-gray-600 mt-6">
                          填报日期：{new Date().getFullYear()} 年 {new Date().getMonth() + 1} 月 {new Date().getDate()} 日
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 底部操作栏 */}
                  <div className="sticky bottom-0 bg-white px-8 py-4 border-t border-gray-200 rounded-b-2xl flex items-center justify-between">
                    <div className="flex items-center text-sm text-gray-500">
                      <i className="ri-information-line mr-1.5 text-[#0c7362]"></i>
                      共 {dataPreview.length} 个品种 · {reportPeriod} · {reportType}
                    </div>
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => setPreviewModalOpen(false)}
                        className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 text-sm font-medium whitespace-nowrap cursor-pointer"
                      >
                        关闭预览
                      </button>
                      <button
                        onClick={() => {
                          setPreviewModalOpen(false);
                          handleGenerate();
                        }}
                        className="px-8 py-2.5 bg-gradient-to-r from-[#0c7362] to-[#085b4d] text-white rounded-xl hover:shadow-lg text-sm font-medium whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-download-cloud-line mr-2"></i>确认生成并下载
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 历史报送详情弹窗 */}
          {historyDetailOpen && selectedHistory && (
            <HistoryDetailModal
              record={selectedHistory}
              onClose={() => setHistoryDetailOpen(false)}
            />
          )}
        </div>
      </div>
    </div>
  );
}
